import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

// Fields the admin can edit inline. `id`, `sku`, and image URLs are managed
// separately (image manager modal, sync layer). We deliberately do NOT expose
// stock_qty here — admins should use dedicated inventory tooling for that.
const FIELDS = [
  { key: 'name', label: 'Product name', type: 'text', col: 2 },
  { key: 'brand', label: 'Brand', type: 'text' },
  { key: 'category', label: 'Product type', type: 'text' },
  { key: 'sub_type', label: 'Sub type', type: 'text' },
  { key: 'variant', label: 'Variant', type: 'text' },
  { key: 'upc', label: 'UPC', type: 'text' },
  { key: 'wholesale_price', label: 'Wholesale $', type: 'number', step: '0.01' },
  { key: 'msrp', label: 'MSRP $', type: 'number', step: '0.01' },
  { key: 'supplier', label: 'Vendor / supplier (private)', type: 'suggest', list: 'edit-supplier-suggest' },
  { key: 'supplier_sku', label: 'Vendor SKU (private)', type: 'text' },
  { key: 'supplier_discount_pct', label: '% off wholesale (auto-fills cost)', type: 'number', step: '0.1', hint: 'e.g. Westridge = 46' },
  { key: 'supplier_cost', label: 'Vendor cost $ (or leave blank to derive)', type: 'number', step: '0.01' },
  { key: 'case_pack_size', label: 'Case pack', type: 'number' },
  { key: 'min_order_qty', label: 'MOQ', type: 'number' },
  { key: 'stock_status', label: 'Stock status', type: 'select', options: ['in_stock', 'out_of_stock_coming_soon', 'discontinued'] },
  { key: 'description', label: 'Description', type: 'textarea', col: 2 },
];

export default function ProductEditDialog({ product, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!product) return;
    const seed = {};
    for (const f of FIELDS) seed[f.key] = product[f.key] ?? '';
    setForm(seed);
  }, [product?.id]);

  useEffect(() => {
    if (!open) return;
    api.get('/admin/suppliers').then(({ data }) => setSuppliers(data || [])).catch(() => {});
  }, [open]);

  if (!product) return null;

  const set = (k, v) => setForm((prev) => {
    const next = { ...prev, [k]: v };
    if (k === 'supplier') {
      const match = suppliers.find(
        (s) => (s.name || '').toLowerCase() === (v || '').trim().toLowerCase(),
      );
      // Only auto-prefill when this product doesn't already have a discount %
      // or explicit cost captured — never stomp a value the admin (or the
      // previous save) already wrote.
      if (match && match.default_discount_pct != null && !prev.supplier_discount_pct && !prev.supplier_cost) {
        next.supplier_discount_pct = String(match.default_discount_pct);
      }
    }
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of FIELDS) {
        let v = form[f.key];
        if (f.type === 'number') {
          v = v === '' || v == null ? null : Number(v);
          if (Number.isNaN(v)) v = 0;
        } else if (typeof v === 'string') {
          v = v.trim();
        }
        payload[f.key] = v;
      }
      const { data } = await api.patch(`/products/${product.id}`, payload);
      toast.success('Product updated');
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="product-edit-dialog">
        <DialogHeader>
          <DialogTitle className="h-display text-2xl">Edit product</DialogTitle>
          <p className="sans text-xs uppercase tracking-widest text-neutral-500">
            {product.sku}
          </p>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-2 max-h-[65vh] overflow-y-auto pr-1">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.col === 2 ? 'col-span-2' : ''}>
              <label className="eyebrow mb-1 block">{f.label}</label>
              {f.type === 'textarea' ? (
                <textarea
                  data-testid={`edit-${f.key}`}
                  value={form[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  rows={4}
                  className="ije-input w-full resize-y"
                />
              ) : f.type === 'select' ? (
                <select
                  data-testid={`edit-${f.key}`}
                  value={form[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="ije-input h-10 w-full"
                >
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === 'suggest' ? (
                <>
                  <input
                    data-testid={`edit-${f.key}`}
                    list={f.list}
                    value={form[f.key] ?? ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    className="ije-input h-10 w-full"
                    placeholder="e.g. Doc Johnson, NeuroSource, or new vendor"
                  />
                  <datalist id={f.list}>
                    {suppliers.map((s) => (
                      <option key={s.name} value={s.name}>{`${s.product_count} product${s.product_count === 1 ? '' : 's'}`}</option>
                    ))}
                  </datalist>
                </>
              ) : (
                <input
                  data-testid={`edit-${f.key}`}
                  type={f.type}
                  step={f.step}
                  value={form[f.key] ?? ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="ije-input h-10 w-full"
                />
              )}
              {f.hint && (
                <p className="sans text-[10px] text-neutral-500 mt-1">{f.hint}</p>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="mt-4 gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="btn-outline h-10"
            data-testid="edit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary h-10 min-w-[140px]"
            data-testid="edit-save"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save changes'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
